import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useCreatePromotionalCode } from "../../hooks/useCreatePromotionalCode";
import { useUpdatePromotionalCode } from "../../hooks/useUpdatePromotionalCode";
import {
  DISCOUNT_TYPE_OPTIONS,
  DISCOUNT_TYPES,
  COUPON_TYPE_OPTIONS,
  CODE_STATUS,
  formatPromotionalCode,
  validateCodeFormat,
  COUPON_TYPES,
} from "utils/promotionalCode";

const PromotionalCodeFormModal = ({ show, onHide, existingCode = null }) => {
  const [code, setCode] = useState("");
  const [discountType, setDiscountType] = useState("");
  const [couponType, setCouponType] = useState("");
  const [status, setStatus] = useState(CODE_STATUS.ACTIVE);
  const [expiresAt, setExpiresAt] = useState(null);
  const [maxUses, setMaxUses] = useState("");
  const [description, setDescription] = useState("");
  const [discountAmount, setDiscountAmount] = useState("");
  const [errors, setErrors] = useState({});

  const { mutate: createCode, isPending: isCreating } =
    useCreatePromotionalCode();
  const { mutate: updateCode, isPending: isUpdating } =
    useUpdatePromotionalCode();

  const isEditMode = !!existingCode;
  const isPending = isCreating || isUpdating;
  const isCodeUsed = existingCode?.usedCount > 0;

  useEffect(() => {
    if (existingCode) {
      setCode(existingCode.code || "");
      setDiscountType(existingCode.discountType || "");
      setCouponType(existingCode.couponType || "");
      setStatus(existingCode.status || CODE_STATUS.ACTIVE);
      setExpiresAt(
        existingCode.expiresAt ? new Date(existingCode.expiresAt) : null
      );
      setMaxUses(existingCode.maxUses?.toString() || "");
      setDescription(existingCode.description || "");
      setDiscountAmount(existingCode.metadata?.discountAmount?.toString() || "");
      setErrors({});
    } else {
      setCode("");
      setDiscountType("");
      setCouponType("");
      setStatus(CODE_STATUS.ACTIVE);
      setExpiresAt(null);
      setMaxUses("");
      setDescription("");
      setDiscountAmount("");
      setErrors({});
    }
  }, [existingCode, show]);

  const handleCouponTypeChange = (newCouponType) => {
    setCouponType(newCouponType);
    // Set default maxUses for particular codes
    if (newCouponType === COUPON_TYPES.PARTICULAR && !isEditMode) {
      setMaxUses("1");
    }
    if (errors.couponType) {
      setErrors((prev) => ({ ...prev, couponType: null }));
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.toUpperCase();
    setCode(value);
    if (errors.code) {
      setErrors((prev) => ({ ...prev, code: null }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = {};

    // Validate code
    const codeValidation = validateCodeFormat(code);
    if (!codeValidation.valid) {
      validationErrors.code = codeValidation.error;
    }

    // Validate discount type
    if (!discountType) {
      validationErrors.discountType = "Discount type is required";
    }

    // Validate discount amount for custom type
    if (discountType === DISCOUNT_TYPES.CUSTOM_SETUP_FEE_DISCOUNT) {
      if (!discountAmount) {
        validationErrors.discountAmount = "Discount amount is required";
      } else if (isNaN(discountAmount) || parseFloat(discountAmount) <= 0) {
        validationErrors.discountAmount = "Discount amount must be greater than 0";
      }
    }

    // Validate coupon type
    if (!couponType) {
      validationErrors.couponType = "Coupon type is required";
    }

    // For particular type, expiry date is required
    if (couponType === COUPON_TYPES.PARTICULAR && !expiresAt) {
      validationErrors.expiresAt =
        "Expiry date is required for particular coupon type";
    }

    // Validate expiry date is in future
    if (expiresAt && expiresAt <= new Date()) {
      validationErrors.expiresAt = "Expiry date must be in the future";
    }

    // Validate max uses
    if (maxUses && (isNaN(maxUses) || parseInt(maxUses) < 1)) {
      validationErrors.maxUses = "Max uses must be 1 or greater";
    }

    // In edit mode, validate maxUses is not below usedCount
    if (isEditMode && maxUses && existingCode?.usedCount) {
      if (parseInt(maxUses) < existingCode.usedCount) {
        validationErrors.maxUses = `Max uses cannot be less than current usage (${existingCode.usedCount})`;
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    const body = {
      code: formatPromotionalCode(code),
      discountType,
      couponType,
      status,
      ...(expiresAt && { expiresAt: expiresAt.toISOString() }),
      ...(maxUses && { maxUses: parseInt(maxUses) }),
      ...(description && { description }),
      ...(discountType === DISCOUNT_TYPES.CUSTOM_SETUP_FEE_DISCOUNT && {
        metadata: { discountAmount: parseFloat(discountAmount) }
      }),
    };

    if (isEditMode) {
      updateCode(
        { id: existingCode._id, body },
        {
          onSuccess: () => {
            handleClose();
          },
        }
      );
    } else {
      createCode(body, {
        onSuccess: () => {
          handleClose();
        },
      });
    }
  };

  const handleClose = () => {
    setCode("");
    setDiscountType("");
    setCouponType("");
    setStatus(CODE_STATUS.ACTIVE);
    setExpiresAt(null);
    setMaxUses("");
    setDescription("");
    setDiscountAmount("");
    setErrors({});
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditMode ? "Edit Promotional Code" : "Create Promotional Code"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          {isEditMode && isCodeUsed && (
            <Alert variant="warning" className="mb-3">
              This code has been used {existingCode.usedCount} time(s). Some
              fields may be restricted.
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Code *</Form.Label>
            <Form.Control
              type="text"
              value={code}
              onChange={handleCodeChange}
              placeholder="Enter promotional code (e.g., WELCOME2024)"
              disabled={isEditMode && isCodeUsed}
              isInvalid={!!errors.code}
              maxLength={50}
            />
            <Form.Control.Feedback type="invalid">
              {errors.code}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              3-50 characters, alphanumeric, hyphens, and underscores only
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Discount Type *</Form.Label>
            <Form.Select
              value={discountType}
              onChange={(e) => {
                setDiscountType(e.target.value);
                if (errors.discountType) {
                  setErrors((prev) => ({ ...prev, discountType: null }));
                }
              }}
              isInvalid={!!errors.discountType}
            >
              <option value="">Select discount type</option>
              {DISCOUNT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.discountType}
            </Form.Control.Feedback>
          </Form.Group>

          {discountType === DISCOUNT_TYPES.CUSTOM_SETUP_FEE_DISCOUNT && (
            <Form.Group className="mb-3">
              <Form.Label>Discount Amount *</Form.Label>
              <Form.Control
                type="number"
                value={discountAmount}
                onChange={(e) => {
                  setDiscountAmount(e.target.value);
                  if (errors.discountAmount) {
                    setErrors((prev) => ({ ...prev, discountAmount: null }));
                  }
                }}
                placeholder="Enter discount amount"
                min="0.01"
                step="0.01"
                isInvalid={!!errors.discountAmount}
              />
              <Form.Control.Feedback type="invalid">
                {errors.discountAmount}
              </Form.Control.Feedback>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Coupon Type *</Form.Label>
            <Form.Select
              value={couponType}
              onChange={(e) => handleCouponTypeChange(e.target.value)}
              isInvalid={!!errors.couponType}
            >
              <option value="">Select coupon type</option>
              {COUPON_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
              {errors.couponType}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              {couponType === COUPON_TYPES.GLOBAL && "Multiple companies can use this code, each company can only use it once"}
              {couponType === COUPON_TYPES.PARTICULAR && "Only one specific company can use this code"}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Status</Form.Label>
            <Form.Select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value={CODE_STATUS.ACTIVE}>Active</option>
              <option value={CODE_STATUS.INACTIVE}>Inactive</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ marginRight: '10px' }}>
              Expiry Date{" "}
              {couponType === COUPON_TYPES.PARTICULAR && (
                <span className="text-danger">*</span>
              )}
            </Form.Label>

            <DatePicker
              selected={expiresAt}
              onChange={(date) => {
                setExpiresAt(date);
                if (errors.expiresAt) {
                  setErrors((prev) => ({ ...prev, expiresAt: null }));
                }
              }}
              minDate={new Date()}
              dateFormat="MMMM d, yyyy"
              className={`form-control ${errors.expiresAt ? "is-invalid" : ""}`}
              placeholderText="Select expiry date"
              showTimeSelect={false}
            />

            {errors.expiresAt && (
              <div className="invalid-feedback d-block">{errors.expiresAt}</div>
            )}
            {couponType === COUPON_TYPES.PARTICULAR && (
              <Form.Text className="text-muted">
                Required for particular coupon type
              </Form.Text>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Max Uses</Form.Label>
            <Form.Control
              type="number"
              value={maxUses}
              onChange={(e) => {
                setMaxUses(e.target.value);
                if (errors.maxUses) {
                  setErrors((prev) => ({ ...prev, maxUses: null }));
                }
              }}
              placeholder="Leave empty for unlimited"
              min="1"
              isInvalid={!!errors.maxUses}
              disabled={couponType === COUPON_TYPES.PARTICULAR}
            />
            <Form.Control.Feedback type="invalid">
              {errors.maxUses}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              {couponType === COUPON_TYPES.PARTICULAR
                ? "Particular codes are single-use only"
                : isEditMode && existingCode?.usedCount
                  ? `Current usage: ${existingCode.usedCount}. Max uses cannot be less than this.`
                  : "Leave empty for unlimited uses"}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter description (optional)"
              maxLength={500}
            />
            <Form.Text className="text-muted">
              {description.length}/500 characters
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isPending || !code || !discountType || !couponType}
          >
            {isPending
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update Code"
                : "Create Code"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default PromotionalCodeFormModal;
